import React, { useState, useEffect, useMemo } from 'react';
import { View, ScrollView, Pressable, Platform } from 'react-native';
import { useRouter, useLocalSearchParams } from 'expo-router';
import { Image } from 'expo-image';
import { marked } from 'marked';
import DOMPurify from 'isomorphic-dompurify';
import { useTheme } from '@/context/ThemeContext';
import { isDarkTheme } from '@/constants/Colors';
import MetaHead from '@/components/MetaHead';
import Text from '@/components/Typography/Text';
import Icon from '@/components/Icon';
import Button from '@/components/Buttons/Button';
import useResponsiveness from '@/hooks/useResponsiveness';
import PublicFooter from '@/components/PublicFooter';
import FlowField from '@/components/Landing/FlowField';
import { blogApi } from '@/lib/blogApi';
import {
  buildBlogPostStructuredData,
  blogPublicationDates,
  toIsoDate,
} from '@/lib/blogStructuredData';

const _staticMetaCache = {};

export async function generateStaticParams() {
  // Rendered on demand (backend SSR meta + client SPA), not prerendered.
  // /blog/:slug already gets full server-side SEO in backend/routes/appRoutes.js,
  // so the prerendered HTML went unused. Returning [] keeps the export fast.
  return [];
}

// Configure marked for safe, beautiful output
marked.setOptions({
  gfm: true,
  breaks: true,
});

// ============================================================================
// HELPERS
// ============================================================================
const formatDate = (dateString) => {
  if (!dateString) return '';
  const date = new Date(dateString);
  return date.toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
};

const getPostContent = (post) => {
  if (!post) return '';
  return post.contentHtml || post.content_html || post.bodyHtml || post.body_html
    || post.contentMd || post.content_md || post.contentMarkdown
    || post.body || post.content || post.bodyMd || post.body_md || '';
};

const estimateReadingTime = (post) => {
  const source = getPostContent(post);
  const text = source.replace(/<[^>]*>/g, '');
  const words = text.split(/\s+/).filter(Boolean).length;
  return Math.max(1, Math.ceil(words / 200));
};

// ============================================================================
// HTML CONTENT RENDERER (WEB)
// ============================================================================
const BLOG_SANITIZE_CONFIG = {
  ALLOWED_TAGS: [
    'a', 'abbr', 'address', 'article', 'aside', 'b', 'blockquote', 'br', 'caption',
    'cite', 'code', 'col', 'colgroup', 'dd', 'del', 'details', 'dfn', 'div', 'dl', 'dt',
    'em', 'figcaption', 'figure', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'hr', 'i',
    'img', 'ins', 'kbd', 'li', 'mark', 'nav', 'ol', 'p', 'pre', 'q', 's', 'samp',
    'section', 'small', 'span', 'strong', 'sub', 'summary', 'sup', 'table', 'tbody',
    'td', 'tfoot', 'th', 'thead', 'time', 'tr', 'u', 'ul', 'var', 'wbr',
  ],
  ALLOWED_ATTR: ['href', 'title', 'alt', 'src', 'srcset', 'width', 'height', 'loading', 'rel', 'target', 'id', 'name', 'cite', 'datetime', 'colspan', 'rowspan'],
  ALLOWED_URI_REGEXP: /^(?:(?:https?|mailto|tel):|[^a-z]|[a-z+.-]+(?:[^a-z+.\-:]|$))/i,
  FORBID_TAGS: ['script', 'style', 'iframe', 'object', 'embed', 'form', 'input', 'button', 'svg', 'math'],
  FORBID_ATTR: ['style', 'onerror', 'onload', 'onclick', 'onmouseover', 'onfocus', 'onblur', 'onchange', 'onsubmit', 'formaction', 'srcdoc'],
  ALLOW_DATA_ATTR: false,
};

const BlogHtmlContent = ({ html, isDark, colors, isXS }) => {
  const safeHtml = useMemo(() => {
    if (!html) return '';
    try {
      return DOMPurify.sanitize(html, BLOG_SANITIZE_CONFIG);
    } catch {
      return '';
    }
  }, [html]);

  if (Platform.OS !== 'web' || !safeHtml) return null;

  const className = 'blog-article-content';
  const isWeb = Platform.OS === 'web';

  useEffect(() => {
    if (!isWeb) return undefined;
    const style = document.createElement('style');
    style.setAttribute('data-blog-content', 'true');
    style.textContent = `
      .${className} {
        font-family: ui-sans-serif, -apple-system, system-ui, BlinkMacSystemFont, 'Segoe UI', Roboto, 'Helvetica Neue', Arial, sans-serif;
        font-size: ${isXS ? '16px' : '18px'};
        line-height: 1.75;
        color: ${colors.text_color_primary};
        word-break: break-word;
        overflow-wrap: break-word;
      }
      .${className} h1, .${className} h2, .${className} h3,
      .${className} h4, .${className} h5, .${className} h6 {
        color: ${colors.text_color_primary};
        font-weight: 700;
        line-height: 1.3;
        margin-top: 2em;
        margin-bottom: 0.5em;
        letter-spacing: -0.02em;
      }
      .${className} h1 { font-size: ${isXS ? '28px' : '36px'}; }
      .${className} h2 { font-size: ${isXS ? '24px' : '30px'}; }
      .${className} h3 { font-size: ${isXS ? '20px' : '24px'}; }
      .${className} h4 { font-size: ${isXS ? '18px' : '20px'}; }
      .${className} p {
        margin-bottom: 1.25em;
        color: ${colors.text_color_secondary};
      }
      .${className} a {
        color: #8B5CF6;
        text-decoration: underline;
        text-decoration-color: rgba(139, 92, 246, 0.3);
        text-underline-offset: 3px;
        transition: text-decoration-color 0.2s ease;
      }
      .${className} a:hover {
        text-decoration-color: #8B5CF6;
      }
      .${className} img {
        max-width: 100%;
        height: auto;
        border-radius: 16px;
        margin: 1.5em 0;
        display: block;
      }
      .${className} blockquote {
        border-left: 4px solid ${isDark ? 'rgba(255,255,255,0.12)' : 'rgba(0,0,0,0.1)'};
        padding: 1em 1.5em;
        margin: 1.5em 0;
        background: ${isDark ? 'rgba(255,255,255,0.03)' : 'rgba(0,0,0,0.02)'};
        border-radius: 0 12px 12px 0;
        font-style: italic;
        color: ${colors.text_color_secondary};
      }
      .${className} pre {
        background: ${isDark ? 'rgba(0,0,0,0.5)' : 'rgba(0,0,0,0.9)'};
        border-radius: 12px;
        padding: 1.25em 1.5em;
        overflow-x: auto;
        margin: 1.5em 0;
        border: 1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
      }
      .${className} pre code {
        color: #E4E4E7;
        font-size: ${isXS ? '13px' : '14px'};
        line-height: 1.7;
        background: none;
        padding: 0;
        border-radius: 0;
        border: none;
      }
      .${className} code {
        background: ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
        padding: 0.2em 0.5em;
        border-radius: 6px;
        font-size: 0.9em;
        font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace;
      }
      .${className} ul, .${className} ol {
        padding-left: 1.5em;
        margin-bottom: 1.25em;
        color: ${colors.text_color_secondary};
      }
      .${className} li {
        margin-bottom: 0.5em;
        line-height: 1.75;
      }
      .${className} li::marker {
        color: ${isDark ? 'rgba(255,255,255,0.3)' : 'rgba(0,0,0,0.3)'};
      }
      .${className} hr {
        border: none;
        height: 1px;
        background: ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
        margin: 2em 0;
      }
      .${className} table {
        width: 100%;
        border-collapse: collapse;
        margin: 1.5em 0;
        border-radius: 12px;
        overflow: hidden;
        border: 1px solid ${isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.06)'};
      }
      .${className} th, .${className} td {
        padding: 0.75em 1em;
        text-align: left;
        border-bottom: 1px solid ${isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)'};
      }
      .${className} th {
        font-weight: 600;
        background: ${isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.02)'};
      }
      .${className} strong { font-weight: 700; color: ${colors.text_color_primary}; }
      .${className} em { font-style: italic; }
      .${className} figure { margin: 1.5em 0; }
      .${className} figcaption {
        text-align: center;
        font-size: 0.875em;
        color: ${colors.text_color_tertiary};
        margin-top: 0.75em;
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (style.parentNode) style.parentNode.removeChild(style);
    };
  }, [isWeb, isDark, colors, isXS]);

  if (!isWeb || !html) return null;

  return (
    <div
      className={className}
      dangerouslySetInnerHTML={{ __html: safeHtml }}
    />
  );
};

// ============================================================================
// TAG PILL
// ============================================================================
const TagPill = ({ label, isDark, onPress }) => (
  <Pressable
    onPress={onPress}
    style={({ hovered }) => ({
      paddingHorizontal: 12,
      paddingVertical: 6,
      borderRadius: 8,
      backgroundColor: hovered
        ? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)')
        : (isDark ? 'rgba(255,255,255,0.04)' : 'rgba(0,0,0,0.03)'),
      borderWidth: 1,
      borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
      transition: 'all 0.2s ease',
    })}
  >
    <Text size="xs" color="secondary">#{label}</Text>
  </Pressable>
);

// ============================================================================
// MAIN BLOG POST PAGE
// ============================================================================
export default function BlogPostPage() {
  const { colors, theme } = useTheme();
  const { isXS, isSM } = useResponsiveness();
  const router = useRouter();
  const params = useLocalSearchParams();
  const slug = useMemo(() => {
    const raw = params?.slug;
    if (typeof raw === 'string') return raw.trim();
    if (Array.isArray(raw)) return String(raw[0] || '').trim();
    return '';
  }, [params?.slug]);
  const isDark = isDarkTheme(theme);

  const [post, setPost] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const sectionPadding = isXS ? 20 : 40;
  const contentMaxWidth = 740;

  useEffect(() => {
    if (!slug) return;
    const loadPost = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const data = await blogApi.getPostBySlug(slug);
        setPost(data);
      } catch (err) {
        console.error('Blog post load error:', err);
        setError(err.message || 'Failed to load post');
      } finally {
        setIsLoading(false);
      }
    };
    loadPost();
  }, [slug]);

  const readTime = useMemo(() => {
    if (!post) return 0;
    return estimateReadingTime(post);
  }, [post]);

  // Resolve HTML content — use contentHtml only if it has meaningful text,
  // otherwise convert contentMd (the reliable full body source) via marked.
  // Then strip any leading cover image and h1 title that we already render
  // in the page header above.
  const renderedHtml = useMemo(() => {
    if (!post) return null;

    const rawHtml = post.contentHtml || post.content_html || '';
    const md = post.contentMd || post.content_md || post.body || post.content || '';

    // Check if HTML has meaningful text (not just an image tag or whitespace)
    const htmlTextContent = rawHtml.replace(/<[^>]*>/g, '').trim();
    const htmlHasMeaningfulContent = htmlTextContent.length > 100;

    let html = '';

    // Prefer contentHtml only when it actually contains the full article body
    if (rawHtml && htmlHasMeaningfulContent) {
      html = rawHtml;
    } else if (md) {
      // Render contentMd through marked
      try {
        html = marked.parse(md);
      } catch (e) {
        console.error('Markdown parse error:', e);
        html = `<p>${md}</p>`;
      }
    } else if (rawHtml) {
      // Last resort: return whatever HTML we have, even if minimal
      html = rawHtml;
    }

    if (!html) return null;

    // Strip leading cover image that duplicates the hero cover we render above.
    // Remove <img> at the start, or an <img> wrapped in <p>/<figure>.
    if (post.coverImage) {
      html = html
        .replace(/^\s*<(p|figure)[^>]*>\s*<img[^>]*>\s*(<\/\1>)/i, '')
        .replace(/^\s*<img[^>]*>\s*/i, '');
    }

    // Strip leading h1 that duplicates the title we render above.
    html = html.replace(/^\s*<h1[^>]*>[\s\S]*?<\/h1>\s*/i, '');

    // Sanitize: blog HTML may originate from AI generation (web search results may
    // contain hostile snippets) or admin edits. Strip scripts, event handlers,
    // and unsafe URLs before passing to dangerouslySetInnerHTML.
    const sanitized = DOMPurify.sanitize(html, {
      USE_PROFILES: { html: true },
      ADD_ATTR: ['target', 'rel'],
      FORBID_TAGS: ['style'],
      FORBID_ATTR: ['onerror', 'onload', 'onclick'],
    });

    return sanitized.trim() || null;
  }, [post]);

  const staticMeta = slug ? _staticMetaCache[slug] : null;
  const seoTitle = post?.seo?.metaTitle || post?.title || staticMeta?.title || 'Blog Post';
  const seoDescription = post?.seo?.metaDescription || post?.excerpt || staticMeta?.description || '';
  const seoImage = post?.seo?.ogImage || post?.coverImage || staticMeta?.image || undefined;
  const canonicalUrl = post?.seo?.canonicalUrl || (slug ? `/blog/${slug}` : '/blog');

  const publicationDates = useMemo(
    () => (post ? blogPublicationDates(post) : null),
    [post],
  );

  const structuredData = useMemo(() => {
    if (!post) return undefined;
    return buildBlogPostStructuredData(post, { readMinutes: readTime });
  }, [post, readTime]);

  const webStoryAmpUrl = useMemo(() => {
    const raw = post?.seo?.webStoryUrl;
    if (!raw || typeof raw !== 'string') return undefined;
    const trimmed = raw.trim();
    if (!trimmed) return undefined;
    if (trimmed.startsWith('http://') || trimmed.startsWith('https://')) return trimmed;
    return `https://gab.ai${trimmed.startsWith('/') ? trimmed : `/${trimmed}`}`;
  }, [post?.seo?.webStoryUrl]);

  const articleMeta = useMemo(() => {
    if (!post) return undefined;
    return {
      publishedTime: toIsoDate(post.publishedAt),
      modifiedTime: toIsoDate(post.updatedAt || post.publishedAt),
      author: post.author || 'Gab AI',
      section: post.category || undefined,
      tags: post.tags || [],
    };
  }, [post]);

  // Inject scrollbar CSS
  useEffect(() => {
    if (Platform.OS === 'web') {
      const style = document.createElement('style');
      style.textContent = `
        .blog-post-scroll::-webkit-scrollbar { width: 8px; }
        .blog-post-scroll::-webkit-scrollbar-track { background: transparent; }
        .blog-post-scroll::-webkit-scrollbar-thumb { background: rgba(150,150,150,0.2); border-radius: 4px; }
        .blog-post-scroll::-webkit-scrollbar-thumb:hover { background: rgba(150,150,150,0.4); }
      `;
      document.head.appendChild(style);
      return () => { document.head.removeChild(style); };
    }
  }, []);

  return (
    <View style={{ flex: 1, backgroundColor: colors.solid_color_primary }}>
      <FlowField isDark={isDark} />
      <MetaHead
        title={seoTitle}
        description={seoDescription}
        canonical={canonicalUrl}
        image={seoImage}
        type="article"
        keywords={post?.seo?.focusKeyword
          ? [post.seo.focusKeyword, ...(post.seo.longtailKeywords || []), ...(post.tags || [])].join(', ')
          : (post?.tags || []).join(', ')}
        structuredData={structuredData}
        article={articleMeta}
        lastModified={articleMeta?.modifiedTime}
        amphtml={webStoryAmpUrl}
        rssUrl="/json/blog/rss"
      />

      <ScrollView
        className="blog-post-scroll"
        accessibilityRole="main"
        nativeID="main-content"
        showsVerticalScrollIndicator={false}
        contentContainerStyle={{ alignItems: 'center' }}
        style={{ position: 'relative', zIndex: 1 }}
      >
        {/* Loading */}
        {isLoading && (
          <View style={{ paddingTop: isXS ? 120 : 180, paddingBottom: 100, alignItems: 'center' }}>
            <View
              style={{
                width: 48,
                height: 48,
                borderRadius: 24,
                backgroundColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                alignItems: 'center',
                justifyContent: 'center',
                marginBottom: 16,
              }}
            >
              <Icon name="spinner" size={20} color={colors.text_color_tertiary} />
            </View>
            <Text size="s" color="tertiary">Loading article...</Text>
          </View>
        )}

        {/* Error */}
        {!isLoading && error && (
          <View style={{ paddingTop: isXS ? 120 : 180, paddingBottom: 100, alignItems: 'center', paddingHorizontal: 20 }}>
            <Icon name="circle-exclamation" size={36} color={isDark ? 'rgba(255,255,255,0.2)' : 'rgba(0,0,0,0.15)'} />
            <Text size="l" weight="medium" style={{ marginTop: 20 }}>
              Article not found
            </Text>
            <Text size="m" color="secondary" style={{ marginTop: 8, textAlign: 'center', maxWidth: 400 }}>
              {error}
            </Text>
            <Button
              text="Back to Blog"
              variant="secondary"
              size="medium"
              isRound
              prependIcon="arrow-left"
              onPress={() => router.push('/blog')}
              style={{ marginTop: 24 }}
            />
          </View>
        )}

        {/* Post Content */}
        {!isLoading && !error && post && (
          <>
            {/* Cover Image */}
            {post.coverImage && (
              <View
                style={{
                  width: '100%',
                  maxWidth: 1100,
                  paddingHorizontal: sectionPadding,
                  paddingTop: isXS ? 90 : 120,
                }}
              >
                <View
                  style={{
                    width: '100%',
                    aspectRatio: isXS ? 16 / 10 : 16 / 7,
                    borderRadius: isXS ? 16 : 24,
                    overflow: 'hidden',
                    borderWidth: 1,
                    borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.04)',
                  }}
                >
                  <Image
                    source={{ uri: post.coverImage }}
                    style={{ width: '100%', height: '100%' }}
                    contentFit="cover"
                    transition={400}
                    accessibilityLabel={post.title}
                  />
                </View>
              </View>
            )}

            {/* Article Header */}
            <View
              style={{
                width: '100%',
                maxWidth: contentMaxWidth,
                paddingHorizontal: sectionPadding,
                paddingTop: post.coverImage ? (isXS ? 28 : 40) : (isXS ? 100 : 140),
                marginBottom: isXS ? 24 : 36,
              }}
            >
              {/* Back + Category */}
              <View style={{ flexDirection: 'row', alignItems: 'center', gap: 12, marginBottom: isXS ? 16 : 20, flexWrap: 'wrap' }}>
                <Pressable
                  onPress={() => router.push('/blog')}
                  style={({ hovered }) => ({
                    flexDirection: 'row',
                    alignItems: 'center',
                    gap: 6,
                    paddingHorizontal: 12,
                    paddingVertical: 6,
                    borderRadius: 8,
                    backgroundColor: hovered
                      ? (isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)')
                      : 'transparent',
                    transition: 'all 0.2s ease',
                  })}
                >
                  <Icon name="arrow-left" size={12} color={colors.text_color_secondary} />
                  <Text size="s" color="secondary">Blog</Text>
                </Pressable>

                {post.category && post.categorySlug ? (
                  <Pressable
                    onPress={() => router.push(`/blog/category/${post.categorySlug}`)}
                    style={({ hovered }) => ({
                      paddingHorizontal: 12,
                      paddingVertical: 5,
                      borderRadius: 100,
                      backgroundColor: hovered
                        ? 'rgba(139, 92, 246, 0.18)'
                        : 'rgba(139, 92, 246, 0.1)',
                      borderWidth: 1,
                      borderColor: 'rgba(139, 92, 246, 0.2)',
                    })}
                  >
                    <Text size="xs" weight="medium" style={{ color: '#8B5CF6' }}>
                      {post.category}
                    </Text>
                  </Pressable>
                ) : post.category ? (
                  <View
                    style={{
                      paddingHorizontal: 12,
                      paddingVertical: 5,
                      borderRadius: 100,
                      backgroundColor: 'rgba(139, 92, 246, 0.1)',
                      borderWidth: 1,
                      borderColor: 'rgba(139, 92, 246, 0.2)',
                    }}
                  >
                    <Text size="xs" weight="medium" style={{ color: '#8B5CF6' }}>
                      {post.category}
                    </Text>
                  </View>
                ) : null}
              </View>

              {/* Title */}
              <Text
                tag="h1"
                id="blog-speakable-headline"
                nativeID="blog-speakable-headline"
                weight="bold"
                style={{
                  fontSize: isXS ? 32 : isSM ? 40 : 48,
                  lineHeight: isXS ? 40 : isSM ? 48 : 56,
                  letterSpacing: isXS ? -1 : -1.5,
                  marginBottom: isXS ? 16 : 20,
                }}
              >
                {post.title}
              </Text>

              {/* Excerpt */}
              {post.excerpt && (
                <Text
                  tag="p"
                  id="blog-speakable-summary"
                  nativeID="blog-speakable-summary"
                  size="l"
                  color="secondary"
                  style={{
                    lineHeight: isXS ? 28 : 32,
                    fontSize: isXS ? 17 : 20,
                    marginBottom: isXS ? 20 : 24,
                  }}
                >
                  {post.excerpt}
                </Text>
              )}

              {/* Meta Bar */}
              <View
                style={{
                  flexDirection: 'row',
                  alignItems: 'center',
                  gap: 16,
                  paddingVertical: 16,
                  borderTopWidth: 1,
                  borderBottomWidth: 1,
                  borderColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                  flexWrap: 'wrap',
                }}
              >
                {post.author && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 8 }}>
                    <View
                      style={{
                        width: 32,
                        height: 32,
                        borderRadius: 16,
                        backgroundColor: isDark ? 'rgba(255,255,255,0.08)' : 'rgba(0,0,0,0.05)',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon name="user" size={14} color={isDark ? 'rgba(255,255,255,0.4)' : 'rgba(0,0,0,0.3)'} />
                    </View>
                    <Text size="s" weight="medium">{post.author}</Text>
                  </View>
                )}
                {publicationDates?.published && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Icon name="calendar" size={13} color={colors.text_color_tertiary} />
                    <Text tag="time" dateTime={publicationDates.published} size="s" color="tertiary">
                      Published {formatDate(post.publishedAt)}
                    </Text>
                  </View>
                )}
                {publicationDates?.showUpdated && publicationDates.modified && (
                  <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                    <Icon name="pen" size={13} color={colors.text_color_tertiary} />
                    <Text tag="time" dateTime={publicationDates.modified} size="s" color="tertiary">
                      Last updated {formatDate(post.updatedAt)}
                    </Text>
                  </View>
                )}
                <View style={{ flexDirection: 'row', alignItems: 'center', gap: 6 }}>
                  <Icon name="clock" size={13} color={colors.text_color_tertiary} />
                  <Text size="s" color="tertiary">{readTime} min read</Text>
                </View>
              </View>
            </View>

            {/* ──────────────────────────────────────────────────────── */}
            {/* Article Body */}
            {/* ──────────────────────────────────────────────────────── */}
            <View
              style={{
                width: '100%',
                maxWidth: contentMaxWidth,
                paddingHorizontal: sectionPadding,
                paddingBottom: isXS ? 32 : 48,
              }}
            >
              {renderedHtml ? (
                <BlogHtmlContent
                  html={renderedHtml}
                  isDark={isDark}
                  colors={colors}
                  isXS={isXS}
                />
              ) : null}
            </View>

            {/* ──────────────────────────────────────────────────────── */}
            {/* Tags */}
            {/* ──────────────────────────────────────────────────────── */}
            {post.tags?.length > 0 && (
              <View
                style={{
                  width: '100%',
                  maxWidth: contentMaxWidth,
                  paddingHorizontal: sectionPadding,
                  paddingBottom: isXS ? 32 : 48,
                }}
              >
                <View
                  style={{
                    flexDirection: 'row',
                    flexWrap: 'wrap',
                    gap: 8,
                    paddingTop: isXS ? 20 : 28,
                    borderTopWidth: 1,
                    borderTopColor: isDark ? 'rgba(255,255,255,0.06)' : 'rgba(0,0,0,0.06)',
                  }}
                >
                  {post.tags.map((tag) => (
                    <TagPill
                      key={tag}
                      label={tag}
                      isDark={isDark}
                      onPress={() => router.push(`/blog?tag=${encodeURIComponent(tag)}`)}
                    />
                  ))}
                </View>
              </View>
            )}

            {/* ──────────────────────────────────────────────────────── */}
            {/* CTA */}
            {/* ──────────────────────────────────────────────────────── */}
            {post.cta?.gabAiCta && (
              <View
                style={{
                  width: '100%',
                  maxWidth: contentMaxWidth,
                  paddingHorizontal: sectionPadding,
                  paddingBottom: isXS ? 32 : 48,
                }}
              >
                <View
                  style={{
                    padding: isXS ? 24 : 32,
                    borderRadius: 20,
                    backgroundColor: isDark ? 'rgba(139,92,246,0.08)' : 'rgba(139,92,246,0.04)',
                    borderWidth: 1,
                    borderColor: isDark ? 'rgba(139,92,246,0.2)' : 'rgba(139,92,246,0.15)',
                    alignItems: 'center',
                  }}
                >
                  <Text
                    weight="semibold"
                    style={{
                      fontSize: isXS ? 18 : 22,
                      textAlign: 'center',
                      marginBottom: 12,
                    }}
                  >
                    Ready to try Gab AI?
                  </Text>
                  <Text
                    size="m"
                    color="secondary"
                    style={{ textAlign: 'center', marginBottom: 20, maxWidth: 400, lineHeight: 24 }}
                  >
                    One AI for everything — text, images, videos, music, voice, and more. Start free today.
                  </Text>
                  <Button
                    text="Get Started Free"
                    variant="purple"
                    size="large"
                    isRound
                    onPress={() => router.push('/signup')}
                    style={{ paddingHorizontal: 40 }}
                  />
                </View>
              </View>
            )}

            {/* ──────────────────────────────────────────────────────── */}
            {/* Back to Blog */}
            {/* ──────────────────────────────────────────────────────── */}
            <View
              style={{
                width: '100%',
                maxWidth: contentMaxWidth,
                paddingHorizontal: sectionPadding,
                paddingBottom: isXS ? 48 : 64,
                alignItems: 'center',
              }}
            >
              <Button
                text="Back to Blog"
                variant="secondary"
                size="medium"
                isRound
                prependIcon="arrow-left"
                onPress={() => router.push('/blog')}
                style={{
                  paddingHorizontal: 28,
                  backgroundColor: isDark ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.03)',
                }}
              />
            </View>
          </>
        )}

        <PublicFooter />
      </ScrollView>
    </View>
  );
}
